import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

/**
 * Shared Privacy Policy text, rendered both in the landing page footer dialog
 * and on the standalone /privacy-policy page.
 *
 * DRAFT - NOT YET APPROVED. The wording below is derived from what this
 * application demonstrably does (see the sections on uploads, third-party
 * services and browser storage). It has not been reviewed by legal counsel
 * and must not be treated as final.
 *
 * Items that could NOT be determined from this repository and need input
 * from whoever owns the backend and the legal position of the company:
 *   - concrete retention periods (section 8)
 *   - confirmation that server logs do capture IP, browser and device
 *     information as stated in section 2d; this could not be verified from
 *     the frontend and is asserted on the basis of ordinary practice
 *   - whether Cloudflare Web Analytics is actually enabled (section 6);
 *     index.html allows static.cloudflareinsights.com in its CSP, but no
 *     application code loads it
 *   - hosting locations and any backend sub-processors (section 7)
 *   - the registered data protection contact required under PDPA 2010
 */

export const PRIVACY_POLICY_LAST_UPDATED = "4 September 2026";

const PrivacyPolicyContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    {...props}
    className={cn("space-y-4 text-sm text-justify text-gray-700", className)}
  >
    <p className="text-xs text-gray-500">
      Last updated: {PRIVACY_POLICY_LAST_UPDATED}
    </p>

    <h2 className="font-semibold">1. Who we are</h2>
    <p>
      QuickBill is an e-invoice intermediary platform operated by PowerTree
      Solution Sdn. Bhd. (&quot;QuickBill&quot;, &quot;we&quot;,
      &quot;us&quot;), a company based in Johor Bahru, Malaysia. The platform
      connects existing ERP and accounting systems to government e-invoicing
      platforms, including LHDN MyInvois in Malaysia and IRAS InvoiceNow in
      Singapore.
    </p>
    <p>
      This policy explains what personal data the QuickBill website and
      application collect, how that data is used and shared, and the choices
      available to you. It applies to registered users, to people invited to
      join an existing account, and to suppliers who submit documents through
      our self-service pages without holding an account.
    </p>

    <h2 className="font-semibold">2. Data we collect</h2>

    <h3 className="font-semibold">a. Account and business details</h3>
    <p>
      When you register we collect your email address, a password, your business
      name, your Tax Identification Number (TIN), and your business registration
      type and number (for example BRN, NRIC, passport or army number). Within
      the application you may add further business profile information,
      including SST registration numbers, addresses, contact numbers, branch
      details and a company logo image.
    </p>

    <h3 className="font-semibold">b. Document and transaction data</h3>
    <p>
      To create and submit e-invoices we process the contents of your documents.
      This includes buyer and supplier names, TINs, registration and SST
      numbers, email addresses, contact numbers and full postal addresses,
      together with line item descriptions, item codes, quantities, unit prices,
      discounts, tax types and rates, totals, country of origin and tariff
      codes. It also includes the payment details recorded on a document, such
      as payment mode, terms, reference, amount and date. QuickBill does not
      process payments and does not collect card or bank account credentials.
    </p>

    <h3 className="font-semibold">c. Files you upload</h3>
    <p>
      You may upload receipt or invoice images for automated data extraction,
      spreadsheet files for bulk import and mapping profile configuration, and
      image files for your company logo. Uploaded receipts and invoices
      routinely contain the personal data of other people, such as the names,
      addresses and tax identifiers of your buyers and suppliers.
    </p>

    <h3 className="font-semibold">d. Technical data</h3>
    <p>
      Our servers process the information ordinarily required to deliver a web
      application and keep it secure, including IP address, browser and device
      information, request and error logs, and records of sign-in activity.
    </p>

    <h2 className="font-semibold">3. Data you provide about other people</h2>
    <p>
      Much of the data entered into QuickBill describes your buyers, suppliers
      and employees rather than you. For that data you remain responsible for
      deciding what is collected and why, and for having a lawful basis to
      provide it to us. We process it on your instructions in order to deliver
      the service, and we do not use it to market to those individuals.
    </p>

    <h2 className="font-semibold">4. How we use data</h2>
    <ul className="list-disc space-y-1 pl-5">
      <li>Creating and administering accounts and user invitations.</li>
      <li>
        Preparing, validating, submitting, cancelling and monitoring e-invoice
        documents with the relevant government platform.
      </li>
      <li>
        Extracting data from documents you upload, including through AI-assisted
        features.
      </li>
      <li>
        Generating printable documents, reports and QR codes linking to the
        official validation page for a submitted invoice.
      </li>
      <li>
        Sending service communications such as email verification, password
        resets and account invitations.
      </li>
      <li>Responding to your support requests.</li>
      <li>
        Maintaining security, investigating misuse, diagnosing faults and
        improving the reliability of the service.
      </li>
      <li>Meeting tax, regulatory, audit and record-keeping obligations.</li>
    </ul>

    <h2 className="font-semibold">5. Who we share data with</h2>

    <h3 className="font-semibold">a. Government e-invoicing platforms</h3>
    <p>
      Invoice data you choose to submit is transmitted to the relevant tax
      authority platform, being LHDN MyInvois in Malaysia or IRAS InvoiceNow in
      Singapore. What must be included in a submission is determined by those
      authorities, not by us.
    </p>

    <h3 className="font-semibold">b. Google, for document data extraction</h3>
    <p>
      When you use the receipt or invoice scanning feature, the image file is
      uploaded from your browser directly to the Google Gemini API and analysed
      there. The image and the data extracted from it are therefore processed by
      Google under its own terms, on infrastructure outside Malaysia and
      Singapore. Please do not use this feature for documents you are not
      willing to have processed by a third-party AI service.
    </p>

    <h3 className="font-semibold">c. Service providers</h3>
    <p>
      We rely on providers for hosting, content delivery, email delivery and
      document rendering. They may process personal data on our behalf, and only
      as needed to provide those services to us.
    </p>

    <h3 className="font-semibold">d. Legal disclosures</h3>
    <p>
      We may disclose data where required by law or regulation, or where
      necessary to establish, exercise or defend legal claims.
    </p>
    <p>
      We do not sell personal data, and we do not share it for third-party
      advertising.
    </p>

    <h2 className="font-semibold">6. Cookies and browser storage</h2>
    <p>
      QuickBill uses only the cookies and browser storage needed to run the
      application:
    </p>
    <ul className="list-disc space-y-1 pl-5">
      <li>
        A secure, HTTP-only session cookie that keeps you signed in and allows
        your session to be renewed. It cannot be read by JavaScript.
      </li>
      <li>
        A short-lived cookie that limits how often a session renewal can be
        requested.
      </li>
      <li>
        A cookie that remembers whether the navigation sidebar is expanded.
      </li>
      <li>Local storage holding your light or dark theme preference.</li>
      <li>
        Session storage holding unsent document drafts, so that your work is not
        lost while you move between screens. It is cleared when the document is
        saved or the tab is closed.
      </li>
    </ul>
    <p>
      Your access token is held in memory only and is never written to local
      storage. We do not use advertising or cross-site tracking cookies.
    </p>

    <h2 className="font-semibold">7. International transfers</h2>
    <p>
      Some of the services described above process data outside the country in
      which you are located. In particular, documents submitted to the scanning
      feature are processed by Google. Where data is transferred across borders
      we take reasonable steps to ensure it remains subject to appropriate legal
      and contractual protection.
    </p>

    <h2 className="font-semibold">8. Retention</h2>
    <p>
      We keep personal data for as long as your account remains active and for
      as long afterwards as is required to meet tax, regulatory, audit and
      record-keeping obligations, or to resolve disputes. Records relating to
      submitted e-invoices are subject to the statutory retention periods set by
      the relevant tax authority.
    </p>

    <h2 className="font-semibold">9. Security</h2>
    <p>
      The application is served over HTTPS and restricts, through a content
      security policy, the external services the page is permitted to contact.
      Sessions rely on HTTP-only cookies, and access tokens are never persisted
      in browser storage. We apply reasonable administrative and technical
      safeguards, but no method of transmission or storage is completely secure,
      and we cannot guarantee absolute security.
    </p>

    <h2 className="font-semibold">10. Your rights</h2>
    <p>
      Subject to the Personal Data Protection Act 2010 of Malaysia, the Personal
      Data Protection Act 2012 of Singapore and other applicable law, you may
      request access to the personal data we hold about you, ask that inaccurate
      data be corrected, withdraw a consent you have given, or limit how your
      data is processed. Some data must be retained where the law requires it.
      To make a request, use the contact details in section 12.
    </p>

    <h2 className="font-semibold">11. Changes to this policy</h2>
    <p>
      We may update this policy as the service or our legal obligations change.
      The date at the top of this page identifies the current version. Where a
      change materially affects how we handle personal data, we will take
      reasonable steps to notify account holders.
    </p>

    <h2 className="font-semibold">12. Contact us</h2>
    <p>
      For privacy questions, or to make a request about your personal data,
      contact:
    </p>
    <address className="not-italic">
      PowerTree Solution Sdn. Bhd.
      <br />
      34-01 Jalan Ponderosa 2/1, Johor Bahru, Malaysia
      <br />
      <a
        className="text-blue-700 hover:underline"
        href="mailto:admin@powertree.com.my"
      >
        admin@powertree.com.my
      </a>
      <br />
      <a className="text-blue-700 hover:underline" href="tel:+6073532950">
        07-353 2950
      </a>
    </address>
  </div>
));

PrivacyPolicyContent.displayName = "PrivacyPolicyContent";

export default PrivacyPolicyContent;
